// ProjectsV2 — replaces the Compare-tab body of the legacy ProjectsPage with:
//   - a page header (Export PDF / Excel / Refresh all / + Add site actions).
//     The exports (utils/export) download a report of the sites on screen,
//     built from the compare table's own cells. Until 2026-10-13 "Export
//     XLSX" had no handler and "Export PDF" printed a blank page.
//   - a two-column top row (ProjectsMap on the left, SelectedSiteCard on right)
//   - a tab bar (Compare / Pipeline / Timeline) with Compare as default
//   - Compare tab body = new CompareTable
//   - Pipeline / Timeline: legacy content stays reachable at #/projects-legacy
//     during the MVP. Extracting them is a follow-up PR.
//
// Selection state lives in useSelectedSite (hash-driven). Everything on this
// page reads selectedSiteId from that hook; pins, tabs and the compare table
// all react to the same source.

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useProjects } from '../../contexts/ProjectsContext';
import { useIsPremium } from '../../contexts/PremiumContext';
import { useOrg } from '../../contexts/OrgContext';
import PremiumLock from '../PremiumLock/PremiumLock';
import ProjectsMap from './ProjectsMap';
import SelectedSiteCard from './SelectedSiteCard';
import CompareTable from './CompareTable';
import { useOfftakeMany } from './useOfftake';
import PipelineTab from './PipelineTab';
import TimelineTab from './TimelineTab';
import { useSelectedSite } from './useSelectedSite';
import { deriveMode } from './deriveMode';
import '../../styles/ProjectsV2.css';

/**
 * @param {{ active: boolean }} props
 *   `active` = true when the current hash is #/projects or #/projects/:id
 *   (so ProjectsMap can lazy-mount only when the user first navigates here).
 * The project type (datacenter / hydrogen) comes from the projects
 * themselves — the shell picks the most common use_case across the fetched
 * list. Falls back to org.branding.default_use_case (or 'datacenter' if the
 * org hasn't set one) when the list is empty or ties.
 */
export default function ProjectsV2({ active }) {
  const {
    projects: allProjects,
    activeProjects,
    loading,
    error,
    fetchProjects,
    deleteProject,
  } = useProjects();
  // Dropping a project marks it stage='dropped' (ProjectsContext keeps the
  // row so an undo is possible); the page must list ACTIVE projects only —
  // the header badge already did, hence "Projects 3" next to a list of 5.
  const projects = activeProjects ?? (allProjects || []).filter(p => p.stage !== 'dropped');
  const isPremium = useIsPremium();
  const { branding } = useOrg();

  const [tab, setTab] = useState('compare');
  const [exporting, setExporting] = useState(null);     // 'pdf' | 'xlsx' | null
  const [exportError, setExportError] = useState(null);
  const { selectedSiteId, selectSite } = useSelectedSite(projects);

  const baseMode = useMemo(
    () => deriveMode(projects, branding?.default_use_case),
    [projects, branding?.default_use_case],
  );
  // Ammonia display mode: hydrogen projects in an org that labels the
  // hydrogen use-case "Ammonia" (North Ammonia). Same engine, different
  // card: siting brief and port & offtake rows in, connectivity rows out.
  // CO₂ feedstock stays (urea and e-fuel side-streams, 2026-10-05).
  // Tenant siting profile from /orgs/current (docs/tenant-siting-profiles.md).
  const ammoniaOrg = branding?.siting_profile?.projects_mode === 'ammonia';
  const mode = baseMode === 'hydrogen' && ammoniaOrg ? 'ammonia' : baseMode;

  // Port & offtake payloads for every site, attached as enrichment._offtake
  // so the schema-driven compare rows can read them. Datacenter orgs skip it.
  const offtakeById = useOfftakeMany(mode === 'datacenter' ? [] : projects);
  const projectsView = useMemo(() => {
    if (mode === 'datacenter') return projects;
    return (projects || []).map(p => (
      offtakeById[p.id]
        ? { ...p, enrichment: { ...(p.enrichment || {}), _offtake: offtakeById[p.id] } }
        : p
    ));
  }, [projects, offtakeById, mode]);

  // The exports read the port rows from those payloads: hold the buttons
  // until every site's has arrived, but no longer than 15 s.
  const portsPending = mode !== 'datacenter' && (projects || []).some(p =>
    p && p.lat != null && p.lon != null && (p.country || 'NO') === 'NO' && !(p.id in offtakeById));
  const [portWaitOver, setPortWaitOver] = useState(false);
  useEffect(() => {
    setPortWaitOver(false);
    const timer = setTimeout(() => setPortWaitOver(true), 15000);
    return () => clearTimeout(timer);
  }, [projects?.length]);
  const exportWaiting = portsPending && !portWaitOver;

  const selectedProject = useMemo(
    () => (projectsView || []).find(p => p.id === selectedSiteId) || null,
    [projectsView, selectedSiteId],
  );


  const handleExport = useCallback(async (kind) => {
    setExporting(kind);
    setExportError(null);
    try {
      const { buildExportModel, exportProjects } = await import('../../utils/export');
      const model = buildExportModel({ projects: projectsView, mode, orgName: branding?.company_name });
      await exportProjects(kind, model);
    } catch (err) {
      setExportError(`The ${kind === 'pdf' ? 'PDF' : 'Excel'} export failed: ${err?.message || err}`);
    } finally {
      setExporting(null);
    }
  }, [projectsView, mode, branding?.company_name]);

  const handleRemove = useCallback((id) => {
    if (!id) return;
    if (!window.confirm('Drop this project?')) return;
    deleteProject?.(id);
  }, [deleteProject]);

  const handleAddSite = useCallback(() => {
    // Placeholder — the legacy Projects page has a wizard for this.
    // For MVP, deep-link into the map view so the user can search + save.
    window.location.hash = '#/';
  }, []);

  // Pin-click handler: opens the site-search deep-analysis view for the
  // clicked project. Reuses the entry path VanirSiteScreener already
  // listens to (hash → lookupParcelAtCoordinates → selectNearbyTeig →
  // AnalysisPanel opens). No new route, no duplicate mount.
  const handleOpenSite = useCallback((project) => {
    if (!project || project.lat == null || project.lon == null) return;
    const q = new URLSearchParams({
      lat: String(project.lat),
      lon: String(project.lon),
      project: String(project.id),
    });
    window.location.hash = `#/?${q.toString()}`;
  }, []);

  // Empty / single / many
  const nSites = projects?.length ?? 0;

  return (
    <div className="pv2-page">
      <header className="pv2-page-head">
        <div className="pv2-page-title">
          Projects <span className="pv2-page-count">{nSites}</span>
        </div>
        <div className="pv2-actions">
          <button className="pv2-btn" onClick={fetchProjects} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh all'}
          </button>
          {isPremium ? (
            <>
              <button className="pv2-btn" onClick={() => handleExport('pdf')} disabled={!nSites || !!exporting || exportWaiting}
                      title={exportWaiting ? 'Loading port data for the export…' : undefined}>
                {exporting === 'pdf' ? 'Preparing PDF…' : 'Export PDF'}
              </button>
              <button className="pv2-btn" onClick={() => handleExport('xlsx')} disabled={!nSites || !!exporting || exportWaiting}
                      title={exportWaiting ? 'Loading port data for the export…' : undefined}>
                {exporting === 'xlsx' ? 'Preparing Excel…' : 'Export Excel'}
              </button>
            </>
          ) : (
            <>
              <PremiumLock category="Export PDF" variant="lock">
                <button className="pv2-btn">Export PDF</button>
              </PremiumLock>
              <PremiumLock category="Export Excel" variant="lock">
                <button className="pv2-btn">Export Excel</button>
              </PremiumLock>
            </>
          )}
          <button className="pv2-btn pv2-btn-primary" onClick={handleAddSite}>+ Add site</button>
        </div>
      </header>

      {error && <div className="pv2-error">{String(error.message || error)}</div>}
      {exportError && <div className="pv2-error" role="alert">{exportError}</div>}

      {loading && nSites === 0 && (
        <div className="pv2-skeleton">
          <div className="pv2-skeleton-top">
            <div className="pv2-skeleton-map" />
            <div className="pv2-skeleton-card" />
          </div>
          <div className="pv2-skeleton-table" />
        </div>
      )}

      {nSites === 0 && !loading && (
        <div className="pv2-empty">
          <div className="pv2-empty-title">No saved projects</div>
          <p className="pv2-empty-msg">
            Head to the <a href="#/">Map</a> to search parcels and save them here.
          </p>
        </div>
      )}

      {nSites > 0 && (
        <>
          <div className="pv2-top">
            <div className="pv2-top-map">
              <ProjectsMap
                active={active}
                projects={projects}
                selectedSiteId={selectedSiteId}
                onSelectSite={selectSite}
                onOpenSite={handleOpenSite}
              />
            </div>
            <div className="pv2-top-card">
              <SelectedSiteCard project={selectedProject} mode={mode} />
            </div>
          </div>

          <div className="pv2-tabs">
            <button
              className={`pv2-tab ${tab === 'compare' ? 'pv2-tab-active' : ''}`}
              onClick={() => setTab('compare')}
            >
              Compare {nSites > 0 ? `(${nSites})` : ''}
            </button>
            <button
              className={`pv2-tab ${tab === 'pipeline' ? 'pv2-tab-active' : ''}`}
              onClick={() => setTab('pipeline')}
            >
              Pipeline
            </button>
            <button
              className={`pv2-tab ${tab === 'timeline' ? 'pv2-tab-active' : ''}`}
              onClick={() => setTab('timeline')}
            >
              Timeline
            </button>
          </div>

          <div className="pv2-tab-body">
            {tab === 'compare' && (
              nSites === 1
                ? <div className="pv2-note">Only one project saved — nothing to compare yet.</div>
                : (
                    <CompareTable
                      projects={projectsView}
                      selectedSiteId={selectedSiteId}
                      onSelectSite={selectSite}
                      onRemoveSite={handleRemove}
                      mode={mode}
                    />
                  )
            )}
            {tab === 'pipeline' && <PipelineTab />}
            {tab === 'timeline' && <TimelineTab />}
          </div>
        </>
      )}
    </div>
  );
}
